import React, { useEffect, useRef, useState } from "react";
import "./customCursor.css";
import { cursorState } from "./cursorState";
import { resolveCursorTarget } from "./cursorSelectors";

const CustomCursor = () => {
  const dotRef = useRef(null);
  const ringRef = useRef(null);
  const rippleRef = useRef(null);
  const labelRef = useRef(null);
  const rafId = useRef(null);

  // Mutable pointer coordinates (Zero React re-renders on movement)
  const targetX = useRef(-100);
  const targetY = useRef(-100);
  const ringX = useRef(-100);
  const ringY = useRef(-100);
  const isLoopRunning = useRef(false);

  // Semantic state (low frequency updates)
  const [semanticMode, setSemanticMode] = useState("default");
  const [semanticLabel, setSemanticLabel] = useState("");
  const [isClickDown, setIsClickDown] = useState(false);
  const [isRippling, setIsRippling] = useState(false);
  const [isHidden, setIsHidden] = useState(true);

  // Programmatic cursorState subscription (loading, success, error)
  const [programmaticState, setProgrammaticState] = useState(cursorState.getSnapshot);

  useEffect(() => {
    return cursorState.subscribe(setProgrammaticState);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") return;

    // Check pointer capabilities — only disable on pure touch-only devices (phones/tablets without mouse)
    const isTouchOnly = window.matchMedia("(pointer: coarse) and (hover: none)").matches;
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (isTouchOnly || prefersReducedMotion) {
      return;
    }

    let isMounted = true;
    let rippleTimeout = null;

    // ── PROGRESSIVE ENHANCEMENT & FAIL-SAFE ──
    // Add custom-cursor-ready to activate native cursor suppression
    document.body.classList.add("custom-cursor-ready");

    // Single rAF Motion Loop with Inertial Interpolation (lerp 0.20)
    const runMotionLoop = () => {
      if (!isMounted) return;

      const followFactor = 0.20;
      const dx = targetX.current - ringX.current;
      const dy = targetY.current - ringY.current;

      ringX.current += dx * followFactor;
      ringY.current += dy * followFactor;

      // Update Ring with center translate(-50%, -50%)
      if (ringRef.current) {
        ringRef.current.style.transform = `translate3d(${ringX.current}px, ${ringY.current}px, 0) translate(-50%, -50%)`;
      }

      // Update Ripple Ring with center translate(-50%, -50%)
      if (rippleRef.current) {
        rippleRef.current.style.transform = `translate3d(${ringX.current}px, ${ringY.current}px, 0) translate(-50%, -50%)`;
      }

      // Check if settled to save idle battery
      const distanceRemaining = Math.abs(dx) + Math.abs(dy);
      if (distanceRemaining > 0.1) {
        rafId.current = requestAnimationFrame(runMotionLoop);
      } else {
        isLoopRunning.current = false;
      }
    };

    const startMotionLoop = () => {
      if (!isLoopRunning.current) {
        isLoopRunning.current = true;
        rafId.current = requestAnimationFrame(runMotionLoop);
      }
    };

    // Pointer Movement Handler
    const handleMove = (clientX, clientY, pointerType) => {
      // If a pure touch event occurs, hide custom cursor and restore native cursor
      if (pointerType === "touch") {
        document.body.classList.remove("custom-cursor-ready");
        setIsHidden(true);
        return;
      }

      // Ensure custom-cursor-ready is present when mouse or trackpad moves
      if (!document.body.classList.contains("custom-cursor-ready")) {
        document.body.classList.add("custom-cursor-ready");
      }

      setIsHidden(false);

      targetX.current = clientX;
      targetY.current = clientY;

      // Inner dot tracks instantaneous coordinates directly with center offset
      if (dotRef.current) {
        dotRef.current.style.transform = `translate3d(${clientX}px, ${clientY}px, 0) translate(-50%, -50%)`;
      }

      startMotionLoop();
    };

    const onPointerMove = (e) => {
      handleMove(e.clientX, e.clientY, e.pointerType);
    };

    const onMouseMove = (e) => {
      handleMove(e.clientX, e.clientY, "mouse");
    };

    // Delegated Hover Target Resolution (Runs only when target changes, not per frame)
    const onPointerOver = (e) => {
      const target = e.target;
      if (!target || !(target instanceof HTMLElement)) return;

      const resolved = resolveCursorTarget(target);
      if (resolved.mode === "native") {
        setIsHidden(true);
      } else {
        setIsHidden(false);
        setSemanticMode(resolved.mode);
        setSemanticLabel(resolved.label);
      }
    };

    // Click Interactions
    const onPointerDown = (e) => {
      if (e.pointerType === "touch") return;
      setIsClickDown(true);
    };

    const onPointerUp = (e) => {
      if (e.pointerType === "touch") return;
      setIsClickDown(false);

      // Trigger one-shot micro ripple
      setIsRippling(true);
      if (rippleTimeout) clearTimeout(rippleTimeout);
      rippleTimeout = setTimeout(() => {
        setIsRippling(false);
      }, 240);
    };

    // Visibility & Lifecycle Handlers
    const onMouseLeave = () => {
      setIsHidden(true);
    };

    const onMouseEnter = (e) => {
      targetX.current = e.clientX;
      targetY.current = e.clientY;
      ringX.current = e.clientX;
      ringY.current = e.clientY;
      setIsHidden(false);
      startMotionLoop();
    };

    const onVisibilityChange = () => {
      if (document.hidden) {
        setIsHidden(true);
        if (rafId.current) cancelAnimationFrame(rafId.current);
        isLoopRunning.current = false;
      }
    };

    const onWindowBlur = () => {
      setIsHidden(true);
    };

    // Attach delegated events
    window.addEventListener("pointermove", onPointerMove, { passive: true });
    window.addEventListener("mousemove", onMouseMove, { passive: true });
    window.addEventListener("pointerdown", onPointerDown, { passive: true });
    window.addEventListener("pointerup", onPointerUp, { passive: true });
    document.addEventListener("pointerover", onPointerOver, { passive: true });
    document.documentElement.addEventListener("mouseleave", onMouseLeave);
    document.documentElement.addEventListener("mouseenter", onMouseEnter);
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("blur", onWindowBlur);

    return () => {
      isMounted = false;
      document.body.classList.remove("custom-cursor-ready");
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("pointerup", onPointerUp);
      document.removeEventListener("pointerover", onPointerOver);
      document.documentElement.removeEventListener("mouseleave", onMouseLeave);
      document.documentElement.removeEventListener("mouseenter", onMouseEnter);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("blur", onWindowBlur);
      if (rafId.current) cancelAnimationFrame(rafId.current);
      if (rippleTimeout) clearTimeout(rippleTimeout);
    };
  }, []);

  // Effective Mode (Programmatic overrides take priority over hover)
  const activeMode = programmaticState.activeMode !== "default"
    ? programmaticState.activeMode
    : semanticMode;

  const activeLabel = programmaticState.label || semanticLabel;

  // Compose CSS class names
  const dotClasses = [
    "cursor-dot",
    `is-${activeMode}`,
    isClickDown ? "is-click-down" : "",
    isHidden ? "is-hidden" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const ringClasses = [
    "cursor-ring",
    `is-${activeMode}`,
    isClickDown ? "is-click-down" : "",
    isHidden ? "is-hidden" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const rippleClasses = [
    "cursor-ripple",
    isRippling ? "is-rippling" : "",
    isHidden ? "is-hidden" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div id="goafreet-cursor" aria-hidden="true">
      <div ref={rippleRef} className={rippleClasses} />
      <div ref={ringRef} className={ringClasses}>
        <span ref={labelRef} className="cursor-label">
          {activeLabel}
        </span>
      </div>
      <div ref={dotRef} className={dotClasses} />
    </div>
  );
};

export default CustomCursor;
